'use client';

import { useCallback, useEffect, useRef } from 'react';
import css from './masonry.module.css';

/**
 * Progressive component for Masonry layout. Uses native CSS grid masonry if available,
 * and falls back to simple grid with negative top margins, recalculated on resize.
 *
 * Heavily inspired by: https://css-tricks.com/a-lightweight-masonry-solution/
 */

export function Masonry({
  children
}: {
  children: React.ReactNode;
}) {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const heights = useRef(new Map()); // hold height of each list item

  const layout = useCallback(() => {
    if (gridRef.current) {
      const gap = parseFloat(getComputedStyle(gridRef.current).rowGap);
      const items = [...(gridRef.current.children as HTMLCollectionOf<HTMLElement>)];
      const columns = getComputedStyle(gridRef.current).gridTemplateColumns.split(' ').length;

      // Check if any element changed its height
      let needsUpdate = 0;
      items.forEach(c => {
        const newHeight = c.getBoundingClientRect().height;
        if (heights.current.get(c) !== newHeight) {
          heights.current.set(c, newHeight);
          needsUpdate++;
        }
      });
      if (needsUpdate) {
        // Restore original positions
        items.forEach(item => {
          item.style.removeProperty('margin-top');
        });

        // Calculate offset between elements and set it as a negative margin
        items.slice(columns).forEach((c, i) => {
          const prevBottom = items[i].getBoundingClientRect().bottom; // bottom edge of item above
          const currentTop = c.getBoundingClientRect().top; // top edge of current item

          c.style.marginTop = `${prevBottom + gap - currentTop}px`;
        });
      }
    }
  }, [gridRef]);
  useEffect(() => {
    if (gridRef.current) {
      // native CSS masonry not supported
      if (getComputedStyle(gridRef.current).gridTemplateRows !== 'masonry') {
        layout();
        window.addEventListener('resize', layout);
        return () => {
          window.removeEventListener('resize', layout);
        };
      }
    }
  }, [gridRef, layout, children]);
  return <div className={css.masonry} ref={gridRef} data-sentry-component="Masonry" data-sentry-source-file="masonry.tsx">
            {children}
        </div>;
}